import { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Search,
  Filter,
  Plus,
  UserCheck,
  UserX,
  CheckSquare,
  XCircle,
} from 'lucide-react';
import { Button, Input } from '@/components/ui';
import { useNotification } from '@/hooks/useNotification';
import { locationUserService } from '../../services/locationUserService';
import { PROGRAM_OPTIONS } from '../../utils/constants';
import { AddUsersModal } from './AddUsersModal';
import type { LocationUser, LocationUserListParams } from '../../types/locationUser';

const PAGE_SIZE = 20;

export const LocationUserManagement = () => {
  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedProgram, setSelectedProgram] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);

  // Selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Modal state
  const [showAddUsersModal, setShowAddUsersModal] = useState(false);

  // Bulk action state
  const [bulkUpdating, setBulkUpdating] = useState(false);

  const { showSuccess, showError, showWarning } = useNotification();

  // Build query params from filter state
  const [filters, setFilters] = useState<LocationUserListParams>({
    page: 1,
    page_size: PAGE_SIZE,
  });

  useEffect(() => {
    const newFilters: LocationUserListParams = {
      page: currentPage,
      page_size: PAGE_SIZE,
      name: searchQuery?.trim() || undefined,
      program_id: selectedProgram || undefined,
      status: (selectedStatus as 'Active' | 'Inactive') || undefined,
      sort_by: 'name',
      sort_order: 'asc',
    };
    setFilters(newFilters);
  }, [searchQuery, currentPage, selectedProgram, selectedStatus]);

  // Fetch location users
  const {
    data: usersData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['location-users', filters],
    queryFn: () => locationUserService.getUsers(filters),
    placeholderData: (prev) => prev,
  });

  const users: LocationUser[] = usersData?.data ?? [];
  const totalUsers = usersData?.total ?? 0;
  const totalPages = Math.ceil(totalUsers / PAGE_SIZE);

  // Clear selection when filters/page change
  useEffect(() => {
    setSelectedIds(new Set());
  }, [filters]);

  // --- Selection helpers ---
  const allOnPageSelected =
    users.length > 0 && users.every((u) => selectedIds.has(u.id));

  const toggleSelectAll = () => {
    if (allOnPageSelected) {
      const next = new Set(selectedIds);
      users.forEach((u) => next.delete(u.id));
      setSelectedIds(next);
    } else {
      const next = new Set(selectedIds);
      users.forEach((u) => next.add(u.id));
      setSelectedIds(next);
    }
  };

  const toggleSelectOne = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      next.add(id);
    }
    setSelectedIds(next);
  };

  // --- Filter handlers ---
  const handleSearch = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const handleProgramChange = (value: string) => {
    setSelectedProgram(value);
    setCurrentPage(1);
  };

  const handleStatusChange = (value: string) => {
    setSelectedStatus(value);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedProgram('');
    setSelectedStatus('');
    setCurrentPage(1);
  };

  // --- Per-row status toggle ---
  const handleToggleStatus = async (user: LocationUser) => {
    const newStatus = user.status === 'Active' ? 'Inactive' : 'Active';
    const action = newStatus === 'Active' ? 'enable' : 'disable';

    if (newStatus === 'Active' && user.impact_status !== 'Active') {
      showError(
        'Cannot Enable',
        `Cannot enable location tracking for ${user.name} because their impact status is "${user.impact_status}".`
      );
      return;
    }

    if (!confirm(`Are you sure you want to ${action} location tracking for ${user.name}?`)) {
      return;
    }

    try {
      await locationUserService.updateUserStatus({ id: user.id, status: newStatus });
      showSuccess('Status Updated', `Location tracking ${action}d for ${user.name}.`);
      refetch();
    } catch (err: any) {
      showError('Update Failed', err?.response?.data?.message || err?.message || `Failed to ${action} location tracking.`);
    }
  };

  // --- Bulk status update ---
  const handleBulkStatusUpdate = useCallback(
    async (newStatus: 'Active' | 'Inactive') => {
      if (selectedIds.size === 0) return;

      const action = newStatus === 'Active' ? 'enable' : 'disable';

      // For enabling, pre-check impact_status
      if (newStatus === 'Active') {
        const ineligible = users.filter(
          (u) => selectedIds.has(u.id) && u.impact_status !== 'Active'
        );
        if (ineligible.length > 0) {
          showWarning(
            'Some Users Ineligible',
            `${ineligible.length} user(s) cannot be enabled because their impact status is not Active. They will be skipped.`
          );
        }
      }

      if (
        !confirm(
          `Are you sure you want to ${action} location tracking for ${selectedIds.size} user(s)?`
        )
      ) {
        return;
      }

      setBulkUpdating(true);
      try {
        // Filter out ineligible users for "enable"
        const idsToUpdate = Array.from(selectedIds).filter((id) => {
          if (newStatus === 'Active') {
            const u = users.find((usr) => usr.id === id);
            return u?.impact_status === 'Active';
          }
          return true;
        });

        if (idsToUpdate.length === 0) {
          showError('No Eligible Users', 'None of the selected users can be enabled.');
          return;
        }

        const results = await Promise.allSettled(
          idsToUpdate.map((id) =>
            locationUserService.updateUserStatus({ id, status: newStatus })
          )
        );

        const successCount = results.filter((r) => r.status === 'fulfilled').length;
        const failCount = idsToUpdate.length - successCount;

        if (failCount === 0) {
          showSuccess(
            'All Updated',
            `Location tracking ${action}d for ${successCount} user(s).`
          );
        } else if (successCount === 0) {
          showError('Update Failed', `All ${failCount} update(s) failed.`);
        } else {
          showWarning(
            'Partial Update',
            `${successCount} updated, ${failCount} failed.`
          );
        }

        setSelectedIds(new Set());
        refetch();
      } catch (err: any) {
        showError('Update Error', err?.message || 'An unexpected error occurred.');
      } finally {
        setBulkUpdating(false);
      }
    },
    [selectedIds, users, refetch, showSuccess, showError, showWarning]
  );

  // --- Add users modal callback ---
  const handleAddUsersSuccess = () => {
    setShowAddUsersModal(false);
    refetch();
  };

  // --- Render helpers ---
  const getInitials = (name: string) => {
    const parts = name.trim().split(/\s+/);
    return parts.length >= 2
      ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
      : (parts[0]?.[0] ?? '').toUpperCase();
  };

  const getImpactStatusBadge = (status: string) => {
    switch (status) {
      case 'Active':
        return 'badge-success';
      case 'Inactive':
        return 'badge-error';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
          <span className="text-red-600">!</span>
        </div>
        <h3 className="text-lg font-medium text-rgf-gray-900 mb-2">
          Error Loading Users
        </h3>
        <p className="text-rgf-gray-600 mb-4">
          {(error as any)?.response?.data?.message || (error as any)?.message || 'Failed to load location users.'}
        </p>
        <Button onClick={() => refetch()}>Try Again</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold leading-7 text-rgf-gray-900 sm:text-3xl">
            Location Tracking Users
          </h1>
          <p className="mt-1 text-sm text-rgf-gray-500">
            Manage users enabled for location tracking across RGF programs.
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4">
          <Button
            icon={<Plus className="h-4 w-4" />}
            onClick={() => setShowAddUsersModal(true)}
          >
            Add Users
          </Button>
        </div>
      </div>

      {/* Bulk Action Bar */}
      {selectedIds.size > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex flex-wrap items-center gap-3">
          <div className="flex items-center">
            <CheckSquare className="h-4 w-4 text-blue-600 mr-2" />
            <span className="text-sm font-medium text-blue-800">
              {selectedIds.size} user(s) selected
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              icon={<UserCheck className="h-4 w-4" />}
              onClick={() => handleBulkStatusUpdate('Active')}
              disabled={bulkUpdating}
              className="!bg-green-600 hover:!bg-green-700 !text-white"
            >
              {bulkUpdating ? 'Updating...' : `Enable Tracking (${selectedIds.size})`}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              icon={<UserX className="h-4 w-4" />}
              onClick={() => handleBulkStatusUpdate('Inactive')}
              disabled={bulkUpdating}
              className="!bg-orange-600 hover:!bg-orange-700 !text-white !border-orange-600"
            >
              {bulkUpdating ? 'Updating...' : `Disable Tracking (${selectedIds.size})`}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              icon={<XCircle className="h-4 w-4" />}
              onClick={() => setSelectedIds(new Set())}
              disabled={bulkUpdating}
            >
              Clear Selection
            </Button>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="card">
        <div className="card-body">
          <div className="space-y-4">
            {/* Search Bar */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <Input
                  placeholder="Search users by name..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  startIcon={<Search className="h-4 w-4" />}
                />
              </div>
              <Button
                variant="secondary"
                icon={<Filter className="h-4 w-4" />}
                onClick={() => setShowFilters(!showFilters)}
              >
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </Button>
            </div>

            {/* Collapsible Filters */}
            {showFilters && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-4 border-t border-rgf-gray-200">
                {/* Program Filter */}
                <div>
                  <label className="block text-sm font-medium text-rgf-gray-700 mb-2">
                    Program
                  </label>
                  <select
                    value={selectedProgram}
                    onChange={(e) => handleProgramChange(e.target.value)}
                    className="w-full px-3 py-2 border border-rgf-gray-300 rounded-lg focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
                  >
                    <option value="">All Programs</option>
                    {PROGRAM_OPTIONS.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Location Status Filter */}
                <div>
                  <label className="block text-sm font-medium text-rgf-gray-700 mb-2">
                    Location Status
                  </label>
                  <select
                    value={selectedStatus}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    className="w-full px-3 py-2 border border-rgf-gray-300 rounded-lg focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
                  >
                    <option value="">All Status</option>
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                  </select>
                </div>

                {/* Clear Filters */}
                <div className="flex items-end">
                  <Button
                    variant="secondary"
                    onClick={clearFilters}
                    className="w-full"
                  >
                    Clear All Filters
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="card">
        <div className="card-body p-0">
          {isLoading ? (
            <div className="p-8 text-center">
              <div className="w-8 h-8 border-2 border-rgf-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-rgf-gray-600">Loading users...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-rgf-gray-600">No users found.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-rgf-gray-200">
                <thead className="bg-rgf-gray-50">
                  <tr>
                    <th className="table-header w-10">
                      <input
                        type="checkbox"
                        checked={allOnPageSelected}
                        onChange={toggleSelectAll}
                        className="rounded border-rgf-gray-300 text-rgf-primary-600 focus:ring-rgf-primary-500"
                      />
                    </th>
                    <th className="table-header">Name</th>
                    <th className="table-header">Email</th>
                    <th className="table-header">Employee ID</th>
                    <th className="table-header">Programs</th>
                    <th className="table-header">Roles</th>
                    <th className="table-header">Location Status</th>
                    <th className="table-header">Impact Status</th>
                    <th className="table-header">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-rgf-gray-200">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-rgf-gray-50">
                      <td className="table-cell">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(user.id)}
                          onChange={() => toggleSelectOne(user.id)}
                          className="rounded border-rgf-gray-300 text-rgf-primary-600 focus:ring-rgf-primary-500"
                        />
                      </td>
                      <td className="table-cell">
                        <div className="flex items-center">
                          <div className="h-10 w-10 bg-rgf-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-sm font-medium text-rgf-primary-700">
                              {getInitials(user.name)}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-rgf-gray-900">
                              {user.name}
                            </div>
                            <div className="text-sm text-rgf-gray-500">
                              {user.district}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm text-rgf-gray-900">{user.email}</div>
                      </td>
                      <td className="table-cell">
                        <div className="text-sm text-rgf-gray-900">{user.employee_id}</div>
                      </td>
                      <td className="table-cell">
                        <div className="flex flex-wrap gap-1">
                          {user.program_id.map((prog) => (
                            <span key={prog} className="badge badge-info text-xs">
                              {prog}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="table-cell">
                        <div className="flex flex-wrap gap-1">
                          {user.role.slice(0, 2).map((r, i) => (
                            <span key={i} className="badge badge-info text-xs">
                              {r}
                            </span>
                          ))}
                          {user.role.length > 2 && (
                            <span className="text-xs text-rgf-gray-500">
                              +{user.role.length - 2} more
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="table-cell">
                        <span
                          className={`badge ${
                            user.status === 'Active' ? 'badge-success' : 'badge-error'
                          }`}
                        >
                          {user.status}
                        </span>
                      </td>
                      <td className="table-cell">
                        <span className={`badge ${getImpactStatusBadge(user.impact_status)}`}>
                          {user.impact_status}
                        </span>
                      </td>
                      <td className="table-cell">
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={
                            user.status === 'Active' ? (
                              <UserX className="h-4 w-4" />
                            ) : (
                              <UserCheck className="h-4 w-4" />
                            )
                          }
                          onClick={() => handleToggleStatus(user)}
                          title={
                            user.status === 'Active'
                              ? 'Disable Location Tracking'
                              : 'Enable Location Tracking'
                          }
                          className={
                            user.status === 'Active'
                              ? 'text-orange-600 hover:text-orange-800'
                              : 'text-green-600 hover:text-green-800'
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalUsers > 0 && (
          <div className="px-6 py-3 bg-rgf-gray-50 border-t border-rgf-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-rgf-gray-700">
                Showing {(currentPage - 1) * PAGE_SIZE + 1} to{' '}
                {Math.min(currentPage * PAGE_SIZE, totalUsers)} of {totalUsers} results
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  Previous
                </Button>
                <span className="text-sm text-rgf-gray-700">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add Users Modal */}
      <AddUsersModal
        isOpen={showAddUsersModal}
        onClose={() => setShowAddUsersModal(false)}
        onSuccess={handleAddUsersSuccess}
      />
    </div>
  );
};
